import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/utils/api";

interface WeeklyReport {
  generatedAt: string;
  lowStock: Record<string, unknown>[];
  openRequests: Record<string, unknown>[];
  activeCheckouts: Record<string, unknown>[];
  recentTransactions: Record<string, unknown>[];
}

export function ReportsWeeklyPage() {
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<WeeklyReport>("/reports/weekly");
        setReport(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load report");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingState message="Generating report…" />;
  if (error) return <ErrorState message={error} />;
  if (!report) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Weekly Report"
        subtitle={`Generated ${new Date(report.generatedAt).toLocaleString()}`}
        actions={
          <Button asChild variant="secondary">
            <Link to="/reports/builder">Report Builder</Link>
          </Button>
        }
      />

      <section className="space-y-2">
        <h3 className="font-semibold text-[#1e3a4f]">Low Stock ({report.lowStock.length})</h3>
        <DataTable
          columns={[
            { key: "part", header: "Part", render: (p) => String(p.part_name) },
            { key: "on", header: "On-hand", className: "text-right", render: (p) => String(p.on_hand) },
            { key: "min", header: "Minimum", className: "text-right", render: (p) => String(p.minimum_stock) },
            { key: "short", header: "Shortage", className: "text-right text-red-700", render: (p) => String(p.shortage) },
          ]}
          rows={report.lowStock}
          rowKey={(p) => String(p.id)}
          emptyMessage="All parts at or above minimum stock."
        />
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-[#1e3a4f]">Open Shipping ({report.openRequests.length})</h3>
        <DataTable
          columns={[
            { key: "num", header: "Shipping #", render: (r) => String(r.request_number) },
            { key: "requestor", header: "Requestor", render: (r) => String(r.requestor) },
            { key: "status", header: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
            { key: "needed", header: "Needed By", render: (r) => String(r.needed_by_date || "—") },
          ]}
          rows={report.openRequests}
          rowKey={(r) => String(r.id)}
        />
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-[#1e3a4f]">Active Checkouts ({report.activeCheckouts.length})</h3>
        <DataTable
          columns={[
            { key: "num", header: "Checkout #", render: (c) => String(c.checkout_number) },
            { key: "to", header: "Checked Out To", render: (c) => String(c.checked_out_to) },
            { key: "due", header: "Due Back", render: (c) => String(c.due_back_date || "—") },
          ]}
          rows={report.activeCheckouts}
          rowKey={(c) => String(c.id)}
        />
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-[#1e3a4f]">Recent Transactions (7 days)</h3>
        <DataTable
          columns={[
            { key: "date", header: "Date", render: (t) => new Date(String(t.created_at)).toLocaleString() },
            { key: "part", header: "Part", render: (t) => String(t.part_name) },
            { key: "type", header: "Type", render: (t) => String(t.type_name) },
            {
              key: "qty",
              header: "Qty",
              className: "text-right",
              render: (t) => String(t.quantity_change),
            },
          ]}
          rows={report.recentTransactions}
          rowKey={(t) => String(t.id)}
        />
      </section>
    </div>
  );
}
